import React, { useState, useEffect } from "react";
import { Table, Button, Card, Row, Col, Modal, Badge } from "react-bootstrap";
import { FaEdit, FaTrash, FaEye, FaPlus, FaUsers } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CourseService from "../../../services/courseService";
import Header from "../../../components/common/Header";

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await CourseService.getAllCourses();
      console.log("=== DEBUGGING ===");
      console.log("1. Full response:", response);
      console.log("2. response.data:", response.data);
      console.log("3. response.data type:", typeof response.data);
      console.log(
        "4. Is response.data an array?",
        Array.isArray(response.data),
      );

      // Try different ways to extract data
      let coursesData = [];

      if (response.data && Array.isArray(response.data)) {
        coursesData = response.data;
        console.log("Extracted via response.data");
      } else if (response && Array.isArray(response)) {
        coursesData = response;
        console.log("Extracted via response");
      } else if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        coursesData = response.data.data;
        console.log("Extracted via response.data.data");
      } else if (response.data && response.data.courses) {
        coursesData = response.data.courses;
        console.log("Extracted via response.data.courses");
      }

      console.log("Final coursesData:", coursesData);
      console.log("Courses count:", coursesData.length);
      setCourses(coursesData);

      if (coursesData.length === 0) {
        console.warn(
          "No courses found! Check if token is valid and courses exist in database.",
        );
      }
    } catch (error) {
      console.error("Error loading courses:", error);
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!courseToDelete) return;

    try {
      await CourseService.deleteCourse(courseToDelete.id);
      toast.success("Course deleted successfully");
      loadCourses();
      setShowDeleteModal(false);
      setCourseToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete course");
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/courses/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/admin/courses/${id}`);
  };

  const handleCreate = () => {
    navigate("/admin/courses/create");
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h2>Course Management</h2>
            <p className="text-muted">Total Courses: {courses.length}</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={handleCreate}>
              <FaPlus className="me-2" /> Create New Course
            </Button>
          </Col>
        </Row>

        <Card>
          <Card.Body>
            {loading ? (
              <div className="text-center py-5" >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No courses found.</p>
                <Button variant="primary" onClick={handleCreate}>
                  <FaPlus className="me-2" /> Create Your First Course
                </Button>
              </div>
            ) : (
              <Table striped bordered hover responsive >
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Course Name</th>
                    <th>Description</th>
                    <th>Price</th>
                    <th>Duration</th>
                    <th>Priority</th>
                    <th>Students</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course, index) => (
                    <tr key={course.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{course.name}</strong>
                        {course.status === "inactive" && (
                          <Badge bg="warning" className="ms-2">
                            Inactive
                          </Badge>
                        )}
                        {course.image_url && (
                          <img
                            src={course.image_url}
                            alt={course.name}
                            style={{
                              width: "30px",
                              height: "30px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              marginLeft: "8px",
                            }}
                          />
                        )}
                      </td>
                      <td>{course.description?.substring(0, 50)}...</td>
                      <td>₹{parseFloat(course.price).toLocaleString()}</td>
                      <td>{course.duration}</td>
                      <td>
                        <Badge bg="secondary">{course.priority || 0}</Badge>
                      </td>
                      <td>
                        <Badge bg="info">
                          <FaUsers className="me-1" />
                          {course.enrolled_students || 0}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="info"
                          size="sm"
                          className="me-2"
                          onClick={() => handleView(course.id)}
                          title="View Details"
                        >
                          <FaEye />
                        </Button>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEdit(course.id)}
                          title="Edit Course"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setCourseToDelete(course);
                            setShowDeleteModal(true);
                          }}
                          title="Delete Course"
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete the course:</p>
            <p className="fw-bold text-danger">{courseToDelete?.name}?</p>
            <p className="text-muted small">
              This action cannot be undone. All course materials will also be
              deleted.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete Course
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default CourseManagement;
